import { hasEmailedSignInMarker } from "../auth/emailed-sign-in-marker";

export const BEARER_ROUTE_PREFIXES = [
  "/verify-magic",
  "/claim-account",
  "/upload/photos",
  "/listing-request/",
  "/m/",
  "/count/",
  "/price-survey/",
  "/quotes/",
  "/alerts/",
  "/newsletter/",
  "/reminders/",
  "/investor-updates/",
  "/orders/",
  "/requests/",
  "/recycler-offers/",
  "/callback",
] as const;

export type PathCredentialPrefix = {
  readonly prefix: string;
  readonly credentialSegments: number;
};

export const PATH_CREDENTIAL_PREFIXES: readonly PathCredentialPrefix[] = [
  { prefix: "/upload/photos", credentialSegments: 1 },
  { prefix: "/listing-request/", credentialSegments: 1 },
  { prefix: "/m/", credentialSegments: 1 },
  { prefix: "/count/", credentialSegments: 2 },
  { prefix: "/price-survey/", credentialSegments: 1 },
];

const CODE_CREDENTIAL_PREFIXES = ["/callback", "/verify-magic", "/api/auth/"] as const;

const IDENTIFYING_PARAMS = new Set(["email", "rfq", "leadid"]);

const CREDENTIAL_PARAMS = new Set([
  "key",
  "secret",
  "signature",
  "sig",
  "auth",
  "password",
  "pwd",
]);

export function isBearerQueryParam(name: string): boolean {
  const lowered = name.toLowerCase();
  return lowered.includes("token") || lowered === "session_id";
}

export function isCredentialQueryParam(
  name: string,
  pathname: string,
): boolean {
  const lowered = name.toLowerCase();
  if (isBearerQueryParam(lowered)) return true;
  if (CREDENTIAL_PARAMS.has(lowered)) return true;
  return (
    lowered === "code" &&
    CODE_CREDENTIAL_PREFIXES.some((prefix) => pathname.startsWith(prefix))
  );
}

export function isRedactableQueryParam(
  name: string,
  pathname: string,
): boolean {
  return (
    IDENTIFYING_PARAMS.has(name.toLowerCase()) ||
    isCredentialQueryParam(name, pathname)
  );
}

function isCredentialFragmentParam(name: string, pathname: string): boolean {
  return name.toLowerCase() === "code" || isCredentialQueryParam(name, pathname);
}

export function isRedactableFragmentParam(
  name: string,
  pathname: string,
): boolean {
  return (
    IDENTIFYING_PARAMS.has(name.toLowerCase()) ||
    isCredentialFragmentParam(name, pathname)
  );
}

export function pathCredentialPrefixFor(
  pathname: string,
): PathCredentialPrefix | null {
  return (
    PATH_CREDENTIAL_PREFIXES.find(({ prefix }) =>
      pathname.startsWith(prefix),
    ) ?? null
  );
}

export const QUERY_CREDENTIAL_PREFIXES = [
  "/reminders/",
  "/investor-updates/",
  "/freight/updates/",
  "/freight/quotes/",
  "/freight/rate-confirmations/",
  "/orders/delivery/",
  "/requests/",
  "/recycler-offers/",
] as const;

const VENDOR_AVAILABILITY_PATH = /^\/orders\/[^/]+\/availability\/?$/;

export function carriesQueryCredential(pathname: string): boolean {
  return (
    QUERY_CREDENTIAL_PREFIXES.some((prefix) => pathname.startsWith(prefix)) ||
    VENDOR_AVAILABILITY_PATH.test(pathname)
  );
}

export function urlCarriesBearerCredential(url: URL): boolean {
  const { pathname } = url;
  const fragment = new URLSearchParams(
    url.hash.slice(1).split("&").filter((pair) => pair.includes("=")).join("&"),
  );
  return (
    pathCredentialPrefixFor(pathname) !== null ||
    carriesQueryCredential(pathname) ||
    hasEmailedSignInMarker(url.hash) ||
    [...url.searchParams.keys()].some((name) => isCredentialQueryParam(name, pathname)) ||
    [...fragment.keys()].some((name) => isCredentialFragmentParam(name, pathname))
  );
}
